import React, { useState } from "react";
import { toast } from "sonner";
import { X, CheckCircle2, Star, Target, Users, BookOpen } from "lucide-react";

export const LeadCaptureModal = ({ isOpen, onClose, defaultPain = "General Confusion", checkoutUrl, buttonText }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    painPoint: defaultPain,
    targetSalary: "10-15 LPA",
    experience: "1-3 Years",
  });
  const [submitting, setSubmitting] = useState(false);

  // Keep formData.painPoint in sync with updated defaultPain
  React.useEffect(() => {
    if (isOpen) {
      setFormData((prev) => ({
        ...prev,
        painPoint: defaultPain
      }));
    }
  }, [defaultPain, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Simulate database write to local storage
    setTimeout(() => {
      const existingLeads = JSON.parse(localStorage.getItem("vph_captured_leads") || "[]");
      const newLead = {
        id: Date.now(),
        ...formData,
        timestamp: new Date().toISOString(),
        sourcePage: window.location.pathname || "/",
      };
      
      localStorage.setItem("vph_captured_leads", JSON.stringify([newLead, ...existingLeads]));

      // Dispatch custom event to sync header count instantly in the same tab
      window.dispatchEvent(new Event("vph_lead_submitted"));

      setSubmitting(false);
      
      // Beautiful trigger toast
      toast.success("Lead Registered Successfully!", {
        description: `Welcome ${formData.name}! Redirecting you to checkout...`,
        action: {
          label: "View Leads",
          onClick: () => {
            window.location.hash = "#admin-leads";
          }
        }
      });

      // Redirect user to the actual TagMango payment/checkout URL
      setTimeout(() => {
        window.location.href = checkoutUrl;
      }, 1200);
      
      onClose();
    }, 1000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md transition-opacity duration-300">
      <div 
        className="relative w-full max-w-lg bg-slate-950 border border-slate-800 rounded-3xl shadow-2xl p-6 overflow-hidden md:p-8"
        data-testid="lead-capture-modal-container"
      >
        {/* Decorative corner glows */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-600/15 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-amber-600/15 rounded-full blur-3xl" />

        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white rounded-full hover:bg-slate-900 transition-colors"
          data-testid="lead-modal-close-btn"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="mb-6">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest bg-amber-500/10 text-amber-500 border border-amber-500/20 mb-3">
            <Star className="h-3 w-3 fill-current" /> High Conversion Pipeline
          </span>
          <h3 className="text-2xl font-black text-white tracking-tight" data-testid="lead-modal-title">
            Secure Your Priority Access
          </h3>
          <p className="text-slate-400 text-sm mt-1.5">
            Fill in your details below. We will save your profile to lock in the special pricing, then redirect you to the booking system.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="lead-capture-form">
          <div>
            <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">
              Full Name
            </label>
            <input
              type="text"
              required
              placeholder="e.g. Rahul Sharma"
              className="w-full px-4 py-3 bg-slate-900/50 border border-slate-800 rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              data-testid="lead-input-name"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">
                Email Address
              </label>
              <input
                type="email"
                required
                placeholder="rahul@example.com"
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-800 rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                data-testid="lead-input-email"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                required
                placeholder="+91 98765 43210"
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-800 rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                data-testid="lead-input-phone"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">
                Experience Level
              </label>
              <select
                className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-white text-sm focus:outline-none focus:border-amber-500 transition-colors"
                value={formData.experience}
                onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                data-testid="lead-select-experience"
              >
                <option value="Fresher">Fresher / Graduate</option>
                <option value="1-3 Years">1-3 Years (Junior)</option>
                <option value="3-6 Years">3-6 Years (Mid-level)</option>
                <option value="6+ Years">6+ Years (Senior)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">
                Target CTC (Salary)
              </label>
              <select
                className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-white text-sm focus:outline-none focus:border-amber-500 transition-colors"
                value={formData.targetSalary}
                onChange={(e) => setFormData({ ...formData, targetSalary: e.target.value })}
                data-testid="lead-select-salary"
              >
                <option value="5-10 LPA">5 - 10 LPA</option>
                <option value="10-15 LPA">10 - 15 LPA</option>
                <option value="15-25 LPA">15 - 25 LPA</option>
                <option value="25+ LPA">25+ LPA (Senior/Lead)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">
              Primary Career Obstacle
            </label>
            <input
              type="text"
              readOnly
              className="w-full px-4 py-3 bg-slate-950 border border-slate-800/80 rounded-xl text-slate-400 text-sm cursor-not-allowed font-medium"
              value={formData.painPoint}
              data-testid="lead-input-obstacle"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full mt-2 py-4 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-black tracking-wide text-sm rounded-xl uppercase flex items-center justify-center gap-2 shadow-lg shadow-amber-500/10 active:scale-[0.99] transition-all duration-150 disabled:opacity-50"
            data-testid="lead-submit-btn"
          >
            {submitting ? (
              <span className="inline-block w-4 h-4 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                {buttonText || "Lock in Slots & Proceed"}
              </>
            )}
          </button>
        </form>

        <div className="mt-4 pt-4 border-t border-slate-900 flex items-center justify-between text-[11px] text-slate-500">
          <span className="flex items-center gap-1">
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> Fully Encrypted SSL
          </span>
          <span>By continuing you agree to VPH Privacy Policies</span>
        </div>
      </div>
    </div>
  );
};
